import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { BLOCKS } from '@contentful/rich-text-types';
import { Section } from '~components/Section';
import { Heading } from '~components/Heading';
import { renderHeading, RichTextRenderer } from '~components/RichTextRenderer';
import { isElementInViewport, smoothScrollTo } from '~utils/dom';
import { TocDesktop } from './TocDesktop';
import { TocMobile } from './TocMobile';

import * as richTextStyles from '../RichTextRenderer/RichTextRenderer.module.css';
import * as styles from './ComponentTextWithToc.module.css';

import type { Document } from '@contentful/rich-text-types';
import type { ContentBlock } from '~components/types';
import type { ContentfulComponentTextWithToc } from './types';

type ComponentTextWithTocProps = {
  content: ContentfulComponentTextWithToc;
};

export const ComponentTextWithToc: ContentBlock<ComponentTextWithTocProps> = ({ content, ...sectionProps }) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState<number>(NaN);
  const contentRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver>();
  const enableObserver = useRef<boolean>(true);
  const sectionsRef = useRef<Element[]>([]);

  const onSelect = (index: number) => {
    enableObserver.current = false;

    smoothScrollTo(sectionsRef.current[index], {
      duration: 500,
      offset: -200,
      callback: () => {
        enableObserver.current = true;
      },
    });

    setActiveSectionIndex(index);
  };

  useEffect(() => {
    if (!contentRef.current) return;

    sectionsRef.current = Array.from(contentRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6'));

    if (!observerRef.current) {
      const observer = new IntersectionObserver((entries) => {
        if (!enableObserver.current) return;

        const intersectingSections = new Set(
          entries.filter((entry) => entry.isIntersecting).map((entry) => entry.target),
        );

        const firstVisibleElement = sectionsRef.current.find(
          (section) => isElementInViewport(section) || intersectingSections.has(section),
        );

        if (firstVisibleElement) {
          const firstVisibleElementIndex = sectionsRef.current.indexOf(firstVisibleElement);

          if (firstVisibleElementIndex !== activeSectionIndex) {
            setActiveSectionIndex(firstVisibleElementIndex);
          }
        }
      });

      observerRef.current = observer;

      sectionsRef.current.forEach((section) => observer.observe(section));
    }
  }, []);

  const headings = getHeadings(content);
  const displayToc = content.displayToc && headings.length > 1;

  return (
    <Section {...sectionProps} className={clsx(styles.section, !displayToc && styles.noToc)}>
      {displayToc && (
        <TocMobile
          className={styles.tocMobile}
          headings={headings}
          activeSectionIndex={activeSectionIndex}
          onSelect={onSelect}
        />
      )}
      {content.heading && (
        <Heading className={clsx(richTextStyles.h2, styles.heading)} level={1}>
          {content.heading}
        </Heading>
      )}
      <RichTextRenderer
        ref={contentRef}
        className={styles.text}
        content={content.text.raw}
        wrapperElement='div'
        options={{
          renderNode: {
            [BLOCKS.HEADING_2]: renderHeading(content.heading ? 2 : 1, 3),
            [BLOCKS.HEADING_3]: renderHeading(content.heading ? 3 : 2, 4),
            [BLOCKS.HEADING_4]: renderHeading(content.heading ? 4 : 3, 5),
          },
        }}
      />
      {displayToc && (
        <TocDesktop
          className={styles.tocDesktop}
          headings={headings}
          activeSectionIndex={activeSectionIndex}
          onSelect={onSelect}
        />
      )}
    </Section>
  );
};

const getHeadings = (content: ContentfulComponentTextWithToc) => {
  const parsedText: Document = JSON.parse(content.text.raw);
  const headings: string[] = [];

  parsedText.content
    .filter((node) => node.nodeType.includes('heading'))
    .forEach((node) => {
      node.content.forEach((subNode) => {
        if (subNode.nodeType === 'text') {
          headings.push(subNode.value);
        }
      });
    });

  return headings;
};

export const query = graphql`
  fragment ComponentTextWithToc on ContentfulComponentTextWithToc {
    __typename
    heading
    text {
      raw
    }
    displayToc
  }
`;
